import { Button, Modal, VisuallyHidden } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { formatDay } from '../format.js';
import { StatusBadge } from '../StatusBadge.js';
import type { ReleaseNote } from './notes.js';
import classes from './WhatsNew.module.css';

/** Notun dili: Türkçe arayüzde `tr`, öteki dillerde `en` (CLAUDE.md "i18n": İngilizce varsayılan). */
function noteLanguage(language: string | undefined): 'en' | 'tr' {
  return language?.startsWith('tr') ? 'tr' : 'en';
}

/** Girişler yayın gününe göre gruplanır (sıra korunur: en yeni başta). */
function byDate(notes: readonly ReleaseNote[]): Array<{ date: string; notes: ReleaseNote[] }> {
  const groups: Array<{ date: string; notes: ReleaseNote[] }> = [];
  for (const note of notes) {
    const last = groups.at(-1);
    if (last?.date === note.date) last.notes.push(note);
    else groups.push({ date: note.date, notes: [note] });
  }
  return groups;
}

export interface WhatsNewContentProps {
  notes: readonly ReleaseNote[];
  /** Görülmemiş girişler: başlığın yanında "New" işareti. */
  unseenIds?: ReadonlySet<string>;
}

/** Yenilikler listesi (M17, ADR 0064): gün başlığı, giriş başlığı ve maddeler. */
export function WhatsNewContent({ notes, unseenIds = new Set() }: WhatsNewContentProps) {
  const { t, i18n } = useTranslation();
  const lang = noteLanguage(i18n.resolvedLanguage);
  return (
    <div className={classes.list}>
      {byDate(notes).map((group) => (
        <section key={group.date} className={classes.day}>
          <h3 className={classes.date}>
            <time dateTime={group.date}>{formatDay(group.date)}</time>
          </h3>
          {group.notes.map((note) => (
            <article key={note.id} className={classes.entry} aria-labelledby={`note-${note.id}`}>
              <div className={classes.entryHead}>
                <h4 id={`note-${note.id}`} className={classes.entryTitle}>
                  {note.title[lang]}
                </h4>
                {unseenIds.has(note.id) ? (
                  <StatusBadge tone="info">{t('settingsScreen.whatsNew.new')}</StatusBadge>
                ) : null}
              </div>
              <ul className={classes.items}>
                {note.items[lang].map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </section>
      ))}
    </div>
  );
}

/** Yenilikler penceresi; About penceresiyle aynı düzen. Kendiliğinden açılmaz. */
export function WhatsNewModal({
  opened,
  onClose,
  ...content
}: WhatsNewContentProps & { opened: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('settingsScreen.whatsNew.title')}
      closeButtonProps={{ 'aria-label': t('settingsScreen.whatsNew.close') }}
      size="lg"
      centered
    >
      <WhatsNewContent {...content} />
      <div className={classes.actions}>
        <Button variant="default" onClick={onClose}>
          {t('settingsScreen.whatsNew.close')}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Görülmemiş not işareti (kullanıcı menüsü öğesinin sağında): küçük nokta; ekran okuyucuya
 * "new notes" metni.
 */
export function WhatsNewDot() {
  const { t } = useTranslation();
  return (
    <>
      <span className={classes.dot} aria-hidden data-testid="whats-new-dot" />
      <VisuallyHidden>{t('settingsScreen.whatsNew.unseen')}</VisuallyHidden>
    </>
  );
}
